import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  page = 'home',
  title = null,
  description = null,
  keywords = null,
  canonical = null 
}) => {
  const { t, i18n } = useTranslation();
  // Prioritize Kinyarwanda (rw) as default language
  const currentLanguage = i18n.language || 'rw';

  // Get the base URL for canonical links
  const baseUrl = 'https://www.abdriverw.com';
  
  // Generate language-specific URL - prioritize rw
  const getLanguageUrl = (lang) => {
    const normalizedLang = lang || 'rw';
    if (normalizedLang === 'en') return baseUrl;
    if (normalizedLang === 'rw' || normalizedLang === 'kiny') return `${baseUrl}/rw`;
    return `${baseUrl}/${normalizedLang}`;
  };

  // Get current page URL with language
  const getCurrentUrl = () => {
    if (canonical) return canonical;
    return getLanguageUrl(currentLanguage);
  };

  // Dynamic meta content based on page and language
  const getMetaContent = () => {
    const pageKey = page || 'home';
    
    return {
      title: title || t(`seo.${pageKey}.title`),
      description: description || t(`seo.${pageKey}.description`),
      keywords: keywords || t(`seo.${pageKey}.keywords`),
    };
  };

  const metaContent = getMetaContent();
  
  // Structured data for the website
  const getStructuredData = () => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "AB Drive",
      "alternateName": currentLanguage === 'fr' ? "Auto-École Rwanda" : currentLanguage === 'rw' ? "Ishuri ry'Amategeko y'Umuhanda" : "Rwanda Driving School",
      "url": baseUrl,
      "logo": `${baseUrl}/images/logo/logo.webp`,
      "image": `${baseUrl}/images/logo/logo.webp`,
      "description": metaContent.description,
      "sameAs": [],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Kigali",
        "addressRegion": "Kigali",
        "addressCountry": "RW",
        "postalCode": "00000"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Rwanda"
      },
      "availableLanguage": [
        {"@type": "Language", "name": "English", "alternateName": "en"},
        {"@type": "Language", "name": "French", "alternateName": "fr"}, 
        {"@type": "Language", "name": "Kinyarwanda", "alternateName": "rw"}
      ],
      "educationalCredentialAwarded": "Driving License Preparation",
      "hasCredential": {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Driving License"
      },
      "keywords": metaContent.keywords,
      "offers": {
        "@type": "Offer",
        "name": currentLanguage === 'fr' ? "Formation Permis de Conduire Rwanda" : currentLanguage === 'rw' ? "Kwiga Amategeko y'Umuhanda" : "Rwanda Driving License Preparation",
        "description": currentLanguage === 'fr' ? "Cours complets pour réussir l'examen permis de conduire Rwanda" : currentLanguage === 'rw' ? "Amasomo yuzuye yo gutsinda ikizamini cy'uruhushya rw'agateganyo rwo Gutwara Ibinyabiziga" : "Complete courses to pass Rwanda driving exam",
        "availability": "https://schema.org/InStock"
      }
    };

    // Add course-specific structured data if on course pages
    if (page === 'course') {
      structuredData["@type"] = "Course";
      structuredData.coursePrerequisites = "Basic literacy";
      structuredData.educationalLevel = "Beginner";
      structuredData.teaches = currentLanguage === 'fr' ? [
        "Règles de circulation au Rwanda",
        "Reconnaissance des panneaux routiers", 
        "Caractéristiques des véhicules",
        "Conditions routières"
      ] : currentLanguage === 'rw' ? [
        "Amategeko y'umuhanda mu Rwanda",
        "Kwiga ibimenyetso by'umuhanda", 
        "Ibiranga ibinyabiziga",
        "Imiterere y'umuhanda"
      ] : [
        "Rwanda traffic rules",
        "Road signs recognition", 
        "Vehicle features",
        "Road conditions"
      ];
      structuredData.provider = {
        "@type": "Organization",
        "name": "AB Drive",
        "url": baseUrl
      };
      structuredData.hasCourseInstance = {
        "@type": "CourseInstance",
        "courseMode": "online",
        "instructor": {
          "@type": "Organization",
          "name": "AB Drive"
        }
      };
    }

    return JSON.stringify(structuredData);
  };

  // FAQ structured data for better search visibility
  const getFAQStructuredData = () => {
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": []
    };

    if (currentLanguage === 'en') {
      faqData.mainEntity = [
        {
          "@type": "Question",
          "name": "How to pass Rwanda driving exam?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "To pass Rwanda driving exam, study Rwanda traffic rules thoroughly, practice with driving exam questions, and take provisional driving test Rwanda preparation courses. AB Drive offers comprehensive driving license preparation Rwanda in 3 languages."
          }
        },
        {
          "@type": "Question", 
          "name": "What are Rwanda traffic rules?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Rwanda traffic rules include road signs, speed limits, traffic regulations, and driving laws. Learn all Rwanda driving exam questions and provisional driving test Rwanda requirements with AB Drive's interactive courses."
          }
        },
        {
          "@type": "Question",
          "name": "Where can I practice Rwanda driving exam questions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Practice real Rwanda driving exam questions at AB Drive. Our platform offers provisional driving test Rwanda practice, driving license preparation Rwanda courses, and comprehensive Rwanda traffic rules training."
          }
        }
      ];
    } else if (currentLanguage === 'fr') {
      faqData.mainEntity = [
        {
          "@type": "Question",
          "name": "Comment réussir l'examen permis de conduire Rwanda?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Pour réussir l'examen permis de conduire Rwanda, étudiez les règles de circulation au Rwanda, pratiquez avec une auto-école Rwanda, et suivez une préparation permis de conduire Rwanda complète. AB Drive offre la meilleure formation."
          }
        },
        {
          "@type": "Question",
          "name": "Quelles sont les règles de circulation au Rwanda?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": "Les règles de circulation au Rwanda incluent les panneaux routiers, limites de vitesse, et lois de conduite. Apprenez avec notre auto-école Rwanda pour votre permis provisoire Rwanda et préparation permis de conduire Rwanda."
          }
        }
      ];
    } else if (currentLanguage === 'rw') {
      faqData.mainEntity = [
        {
          "@type": "Question",
          "name": "Ni gute natsinda ikizamini cy'uruhushya rw'agateganyo rwo Gutwara Ibinyabiziga?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Kugira ngo utsinde ikizamini cy'uruhushya rw'agateganyo rwo Gutwara Ibinyabiziga, iga amategeko y'umuhanda neza, ukore ibibazo bya perimi provisoire, kandi umenye gu twara neza. AB Drive itanga amasomo yuzuye y'amategeko y'umuhanda."
          }
        },
        {
          "@type": "Question",
          "name": "Ni ayahe mategeko y'umuhanda mu Rwanda?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Amategeko y'umuhanda mu Rwanda arimo ibimenyetso by'umuhanda, ibyapa, gu twara neza, n'amategeko y'ibinyabiziga. Wige hamwe na AB Drive kugira ngo utsinde ikizamini cy'uruhushya rw'agateganyo."
          }
        }
      ];
    }

    return JSON.stringify(faqData);
  };

  // Local Business structured data
  const getLocalBusinessData = () => {
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "AB Drive",
      "description": currentLanguage === 'fr' ? "Auto-école Rwanda - Préparation permis de conduire Rwanda" : currentLanguage === 'rw' ? "Ishuri ry'amategeko y'umuhanda - Kwiga amategeko y'umuhanda" : "Rwanda Driving School - Driving License Preparation Rwanda",
      "url": baseUrl,
      "telephone": "+250-XXX-XXX-XXX",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Kigali",
        "addressLocality": "Kigali",
        "addressRegion": "Kigali",
        "postalCode": "00000",
        "addressCountry": "RW"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -1.9441,
        "longitude": 30.0619
      },
      "areaServed": "Rwanda",
      "serviceArea": {
        "@type": "Country",
        "name": "Rwanda"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": currentLanguage === 'fr' ? "Services Auto-École Rwanda" : currentLanguage === 'rw' ? "Serivisi z'Amategeko y'Umuhanda" : "Rwanda Driving School Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": currentLanguage === 'fr' ? "Cours Règles de Circulation Rwanda" : currentLanguage === 'rw' ? "Amasomo y'Amategeko y'Umuhanda" : "Rwanda Traffic Rules Course",
              "description": currentLanguage === 'fr' ? "Formation complète règles de circulation au Rwanda pour examen permis de conduire" : currentLanguage === 'rw' ? "Kwiga amategeko y'umuhanda kugira ngo utsinde ikizamini" : "Complete Rwanda traffic rules training for driving license preparation"
            }
          }
        ]
      }
    });
  };

  // Breadcrumb structured data
  const getBreadcrumbData = () => {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": []
    };

    // Always ensure we have at least one breadcrumb item
    if (page === 'home') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        }
      ];
    } else if (page === 'login' || page === 'signup') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? (page === 'login' ? "Connexion" : "Inscription") : 
                   currentLanguage === 'rw' ? (page === 'login' ? "Kwinjira" : "Kwiyandikisha") : 
                   (page === 'login' ? "Login" : "Sign Up"),
          "item": `${baseUrl}/${page}`
        }
      ];
    } else if (page === 'course') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Cours" : currentLanguage === 'rw' ? "Amasomo" : "Course",
          "item": `${baseUrl}/course`
        }
      ];
    } else if (page === 'quiz') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Quiz" : currentLanguage === 'rw' ? "Ikizamini" : "Quiz",
          "item": `${baseUrl}/quiz`
        }
      ];
    } else if (page === 'dashboard') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Tableau de Bord" : currentLanguage === 'rw' ? "Ikibaho" : "Dashboard",
          "item": `${baseUrl}/dashboard`
        }
      ];
    } else if (page === 'settings') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Paramètres" : currentLanguage === 'rw' ? "Igenamiterere" : "Settings",
          "item": `${baseUrl}/settings`
        }
      ];
    } else if (page === 'support') {
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Support" : currentLanguage === 'rw' ? "Ubufasha" : "Support",
          "item": `${baseUrl}/support`
        }
      ];
    } else {
      // Default breadcrumb for any other page
      breadcrumbData.itemListElement = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": currentLanguage === 'fr' ? "Page" : currentLanguage === 'rw' ? "Urupapuro" : "Page",
          "item": `${baseUrl}/${page}`
        }
      ];
    }

    return JSON.stringify(breadcrumbData);
  };

  // SiteNavigationElement structured data for sitelinks
  const getSiteNavigationData = () => {
    const getNavLabel = (key) => {
      try {
        return t(key);
      } catch {
        return key;
      }
    };

    const navItems = [];
    
    // Home
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Accueil" : currentLanguage === 'rw' ? "Murugo" : "Home",
      "url": getLanguageUrl(currentLanguage)
    });

    // Features
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Fonctionnalités" : currentLanguage === 'rw' ? "Uko Bikora" : "Features",
      "url": `${getLanguageUrl(currentLanguage)}#features`
    });

    // How It Works
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Comment ça marche" : currentLanguage === 'rw' ? "Uko Watangira" : "How It Works",
      "url": `${getLanguageUrl(currentLanguage)}#how-it-works`
    });

    // Why Choose Us
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Pourquoi nous choisir" : currentLanguage === 'rw' ? "Kuki Waduhitamo?" : "Why Choose Us",
      "url": `${getLanguageUrl(currentLanguage)}#why-choose-us`
    });

    // Testimonials
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Témoignages" : currentLanguage === 'rw' ? "Ubuhamya" : "Testimonials",
      "url": `${getLanguageUrl(currentLanguage)}#testimonials`
    });

    // Contact
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Contact" : currentLanguage === 'rw' ? "Twandikire" : "Contact",
      "url": `${getLanguageUrl(currentLanguage)}#contact`
    });

    // Login
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Connexion" : currentLanguage === 'rw' ? "Injira" : "Login",
      "url": `${getLanguageUrl(currentLanguage)}/login`
    });

    // Sign Up
    navItems.push({
      "@type": "SiteNavigationElement",
      "name": currentLanguage === 'fr' ? "Inscription" : currentLanguage === 'rw' ? "Iyandikishe" : "Sign Up",
      "url": `${getLanguageUrl(currentLanguage)}/signup`
    });

    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "AB Drive",
      "url": baseUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${baseUrl}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": navItems
      }
    });
  };

  // ImageObject schema generation helper for image indexing
  const getImageObjectSchema = (imageUrl, imageTitle, imageDescription, imageAlt = '') => {
    const baseUrl = 'https://www.abdriverw.com';
    const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`;
    const currentYear = new Date().getFullYear();
    
    return {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "contentUrl": fullImageUrl,
      "url": fullImageUrl,
      "name": imageTitle,
      "description": imageDescription,
      "caption": imageDescription,
      "alternateText": imageAlt || imageTitle,
      "encodingFormat": imageUrl.includes('.webp') ? "image/webp" : imageUrl.includes('.png') ? "image/png" : imageUrl.includes('.jpg') || imageUrl.includes('.jpeg') ? "image/jpeg" : "image/webp",
      "license": `${baseUrl}`,
      "creditText": "AB Drive",
      "creator": {
        "@type": "Organization",
        "name": "AB Drive"
      },
      "copyrightHolder": {
        "@type": "Organization",
        "name": "AB Drive"
      },
      "copyrightNotice": `© ${currentYear} AB Drive. All rights reserved.`,
      "acquireLicensePage": `${baseUrl}/contact`
    };
  };

  // Course structured data for educational content
  const getCourseData = () => {
    if (page !== 'course') return null;
    
    return JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": currentLanguage === 'fr' ? "Cours Règles de Circulation Rwanda" : 
              currentLanguage === 'rw' ? "Amasomo y'Amategeko y'Umuhanda" : 
              "Rwanda Traffic Rules Course",
      "description": currentLanguage === 'fr' ? "Formation complète pour l'examen permis de conduire Rwanda" : 
                    currentLanguage === 'rw' ? "Amasomo yuzuye yo kwiga amategeko y'umuhanda" : 
                    "Complete course for Rwanda driving license preparation",
      "provider": {
        "@type": "Organization",
        "name": "AB Drive",
        "url": baseUrl
      },
      "educationalLevel": "Beginner",
      "coursePrerequisites": currentLanguage === 'fr' ? "Aucun prérequis" : 
                            currentLanguage === 'rw' ? "Nta byo ukeneye mbere" : 
                            "No prerequisites",
      "teaches": currentLanguage === 'fr' ? [
        "Règles de circulation au Rwanda",
        "Reconnaissance des panneaux routiers",
        "Caractéristiques des véhicules",
        "Conditions routières"
      ] : currentLanguage === 'rw' ? [
        "Amategeko y'umuhanda mu Rwanda",
        "Kwiga ibimenyetso by'umuhanda",
        "Ibiranga ibinyabiziga",
        "Imiterere y'umuhanda"
      ] : [
        "Rwanda traffic rules",
        "Road signs recognition",
        "Vehicle features",
        "Road conditions"
      ],
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "online",
        "instructor": {
          "@type": "Organization",
          "name": "AB Drive"
        }
      }
    });
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{metaContent.title}</title>
      <meta name="description" content={metaContent.description} />
      <meta name="keywords" content={metaContent.keywords} />
      
      {/* Language and Locale */}
      <html lang={currentLanguage} translate="no" />
      <meta property="og:locale" content={currentLanguage === 'en' ? 'en_US' : currentLanguage === 'fr' ? 'fr_FR' : 'rw_RW'} />
      
      {/* Prevent automatic translation */}
      <meta name="google" content="notranslate" />
      <meta name="googlebot" content="notranslate" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={getCurrentUrl()} />
      
      {/* Alternate Language URLs - Prioritize Kinyarwanda */}
      <link rel="alternate" href={getLanguageUrl('rw')} hrefLang="rw" />
      <link rel="alternate" href={getLanguageUrl('en')} hrefLang="en" />
      <link rel="alternate" href={getLanguageUrl('fr')} hrefLang="fr" />
      <link rel="alternate" href={getLanguageUrl('rw')} hrefLang="x-default" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={metaContent.title} />
      <meta property="og:description" content={metaContent.description} />
      <meta property="og:url" content={getCurrentUrl()} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${baseUrl}/images/logo/logo.webp`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={currentLanguage === 'fr' ? "AB Drive - Auto-École Rwanda Formation Permis" : currentLanguage === 'rw' ? "AB Drive - Ishuri ry'Amategeko y'Umuhanda" : "AB Drive - Rwanda Driving License Preparation"} />
      <meta property="og:site_name" content="AB Drive" />
      <meta property="og:locale" content={currentLanguage === 'en' ? 'en_US' : currentLanguage === 'fr' ? 'fr_FR' : 'rw_RW'} />
      <meta property="article:author" content="AB Drive" />
      <meta property="article:publisher" content="AB Drive" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaContent.title} />
      <meta name="twitter:description" content={metaContent.description} />
      <meta name="twitter:image" content={`${baseUrl}/images/logo/logo.webp`} />
      <meta name="twitter:image:alt" content={currentLanguage === 'fr' ? "Auto-École Rwanda - Formation Permis de Conduire" : currentLanguage === 'rw' ? "Ishuri ry'Amategeko y'Umuhanda Rwanda" : "Rwanda Driving School - License Preparation"} />
      <meta name="twitter:creator" content="@ABDriveRwanda" />
      <meta name="twitter:site" content="@ABDriveRwanda" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="AB Drive" />
      <meta name="publisher" content="AB Drive" />
      <meta name="copyright" content="AB Drive 2024" />
      <meta name="revisit-after" content="3 days" />
      <meta name="google-site-verification" content="" />
      <meta name="msvalidate.01" content="" />
      <meta name="yandex-verification" content="" />
      
      {/* Geographic Meta Tags for Rwanda */}
      <meta name="geo.region" content="RW" />
      <meta name="geo.placename" content="Kigali, Rwanda" />
      <meta name="geo.position" content="-1.9441;30.0619" />
      <meta name="ICBM" content="-1.9441, 30.0619" />
      
      {/* Content Type and Page Info */}
      <meta name="page-type" content={page === 'home' ? 'website' : page} />
      <meta name="audience" content="all" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="MobileOptimized" content="320" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* Mobile and Responsive */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Favicon and Logo */}
      <link rel="icon" type="image/x-icon" href="/images/logo/logo.ico" />
      <link rel="shortcut icon" type="image/x-icon" href="/images/logo/logo.ico" />
      <link rel="icon" type="image/webp" href="/images/logo/logo.webp" />
      <link rel="apple-touch-icon" href="/images/logo/logo.webp" />
      
      {/* Enhanced SEO Keywords Meta Tags */}
      <meta name="subject" content={metaContent.keywords.split(',')[0]} />
      <meta name="topic" content={metaContent.keywords.split(',')[0]} />
      <meta name="summary" content={metaContent.description} />
      <meta name="classification" content="Education, Driving, Traffic Rules" />
      <meta name="owner" content="AB Drive" />
      <meta name="directory" content="submission" />
      <meta name="category" content="Education" />
      <meta name="coverage" content="Rwanda" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />
      
      {/* Additional Language Meta Tags */}
      <meta name="language" content={currentLanguage} />
      <meta httpEquiv="content-language" content={currentLanguage} />
      
      {/* Structured Data - Main Organization */}
      <script type="application/ld+json">
        {getStructuredData()}
      </script>
      
      {/* Structured Data - FAQ */}
      <script type="application/ld+json">
        {getFAQStructuredData()}
      </script>
      
      {/* Structured Data - Local Business */}
      <script type="application/ld+json">
        {getLocalBusinessData()}
      </script>
      
      {/* Structured Data - Breadcrumbs */}
      <script type="application/ld+json">
        {getBreadcrumbData()}
      </script>
      
      {/* Structured Data - Site Navigation (Sitelinks) */}
      <script type="application/ld+json">
        {getSiteNavigationData()}
      </script>
      
      {/* Structured Data - Course (if applicable) */}
      {getCourseData() && (
        <script type="application/ld+json">
          {getCourseData()}
        </script>
      )}
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      
      {/* DNS Prefetch for SEO */}
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
    </Helmet>
  );
};

export default SEOHead; 